/**
 * "Auto services" modal — categories like gas stations, garages, etc.
 *
 * Previously this just generated Google Maps / Google Search URLs that
 * kicked the user out of the app. The new flow renders a real results list
 * inline (see `list.view.js`), using data.gov.il for gas/garage/inspection
 * and OpenStreetMap for parking/car-wash/parts. Only the final "navigate"
 * tap still leaves the app (a Google Maps deep link, which is the standard
 * pattern — see Gett, Yango, etc.).
 *
 * Uses the existing `#autoServicesContainer` modal in index.html.
 */

import { createLogger } from '../../core/logger.js';
import * as listView from './list.view.js';

const log = createLogger('auto-services');

const IDS = /** @type {const} */ ({
  container: 'autoServicesContainer',
  cityChip: 'autoCityChip',
  cityInput: 'autoCityInput',
  cityRow: 'autoCityRow',
  cityOk: 'autoCityOk',
  cityCancel: 'autoCityCancel',
  useMyLoc: 'autoUseMyLoc',
  setCity: 'autoSetCity',
  grid: 'autoGrid',
  /** Slot below the category grid where the results list is rendered. The
   *  legacy HTML pre-fills this with "Open in Google Maps / Search Google"
   *  links — we replace those with a placeholder on open, and with the
   *  results list on first category tap. */
  actions: 'autoActions',
  backBtn: 'autoServicesBackBtn',
  closeX: 'autoServicesCloseX',
});

const PLACEHOLDER_HTML =
  '<div class="svc-placeholder">' +
  '<div class="svc-placeholder-icon">🗺️</div>' +
  '<div class="svc-placeholder-title">בחר קטגוריה</div>' +
  '<div class="svc-placeholder-sub">תוצאות יוצגו ישירות כאן — ללא יציאה מהאפליקצייה</div>' +
  '</div>';

let wired = false;

/** Open the auto-services modal. */
export function open() {
  const w = /** @type {any} */ (window);
  const city = typeof w.getUserCityLabel === 'function' ? String(w.getUserCityLabel() ?? '') : '';
  const chip = document.getElementById(IDS.cityChip);
  if (chip) chip.textContent = city ? `מיקום: ${city}` : 'מאתר מיקום…';
  // Replace the legacy "we'll prepare Google links" placeholder with a neutral
  // one. Done on every open so re-opening starts fresh.
  const actions = document.getElementById(IDS.actions);
  if (actions) actions.innerHTML = PLACEHOLDER_HTML;
  // Clear any active card selection from a previous session.
  document.querySelectorAll(`#${IDS.grid} .auto-card`).forEach((c) => c.classList.remove('is-active'));
  wireOnce();
  if (typeof w.openModal === 'function') w.openModal(IDS.container);
  else openInline();
}

/** Close the modal. */
export function close() {
  const w = /** @type {any} */ (window);
  if (typeof w.closeModal === 'function') w.closeModal(IDS.container);
  else closeInline();
}

// ====================================================================
// internals
// ====================================================================

function wireOnce() {
  if (wired) return;
  wired = true;

  const grid = document.getElementById(IDS.grid);
  if (grid) {
    grid.addEventListener('click', (e) => {
      const card = /** @type {HTMLElement | null} */ ((/** @type {HTMLElement} */ (e.target)).closest('.auto-card'));
      if (!card) return;
      // Highlight the active card so the user remembers what they tapped.
      grid.querySelectorAll('.auto-card').forEach((c) => c.classList.remove('is-active'));
      card.classList.add('is-active');

      const term = card.getAttribute('data-term') ?? '';
      const category = listView.termToCategory(term);
      if (category) {
        // Render in-app results — replaces the old Google-links flow.
        void listView.render(category).catch((err) => log.warn('render:', err));
      } else {
        log.warn('unknown term:', term);
      }
    });
  }

  document.getElementById(IDS.useMyLoc)?.addEventListener('click', () => {
    const w = /** @type {any} */ (window);
    if (typeof w.refreshUserLocation === 'function') {
      try { w.refreshUserLocation(); } catch (e) { log.warn('refreshUserLocation:', e); }
    }
    // Clear any city override so we go back to "by current location" mode.
    try { localStorage.removeItem('autome_user_city_override'); } catch { /* ignore */ }
    const city = typeof w.getUserCityLabel === 'function' ? String(w.getUserCityLabel() ?? '') : '';
    const chip = document.getElementById(IDS.cityChip);
    if (chip) chip.textContent = city ? `מיקום: ${city}` : 'מאתר מיקום…';
    refreshActiveCategory();
  });

  document.getElementById(IDS.setCity)?.addEventListener('click', () => toggleCityPicker(true));
  document.getElementById(IDS.cityCancel)?.addEventListener('click', () => toggleCityPicker(false));
  document.getElementById(IDS.cityOk)?.addEventListener('click', () => {
    const input = /** @type {HTMLInputElement | null} */ (document.getElementById(IDS.cityInput));
    const val = (input?.value ?? '').trim();
    if (val) {
      const chip = document.getElementById(IDS.cityChip);
      if (chip) chip.textContent = `מיקום: ${val}`;
      try { localStorage.setItem('autome_user_city_override', val); } catch { /* ignore */ }
    }
    toggleCityPicker(false);
    refreshActiveCategory();
  });

  document.getElementById(IDS.backBtn)?.addEventListener('click', close);
  document.getElementById(IDS.closeX)?.addEventListener('click', close);
}

/** Re-render whichever category is currently active (after location/city
 *  changes). No-op if none is selected yet. */
function refreshActiveCategory() {
  const grid = document.getElementById(IDS.grid);
  const active = /** @type {HTMLElement | null} */ (grid?.querySelector('.auto-card.is-active'));
  if (!active) return;
  const term = active.getAttribute('data-term') ?? '';
  const category = listView.termToCategory(term);
  if (category) void listView.render(category).catch((err) => log.warn('refresh:', err));
}

/** @param {boolean} on */
function toggleCityPicker(on) {
  const row = document.getElementById(IDS.cityRow);
  if (row) row.style.display = on ? 'flex' : 'none';
  if (on) {
    /** @type {HTMLInputElement | null} */ (document.getElementById(IDS.cityInput))?.focus();
  }
}

function openInline() {
  const el = document.getElementById(IDS.container);
  if (!el) return;
  el.setAttribute('aria-hidden', 'false');
  el.classList.add('open');
  el.style.display = 'flex';
  document.body.classList.add('no-scroll');
}

function closeInline() {
  const el = document.getElementById(IDS.container);
  if (!el) return;
  el.setAttribute('aria-hidden', 'true');
  el.classList.remove('open');
  setTimeout(() => { if (el.getAttribute('aria-hidden') === 'true') el.style.display = 'none'; }, 300);
  document.body.classList.remove('no-scroll');
}
